import React, { useState } from 'react';
import { Select, DatePicker, Button } from 'antd';
import { DownloadOutlined, CalendarOutlined } from '@ant-design/icons';
import styled, { createGlobalStyle } from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';
import DownloadButton, { DownloadButtonProps } from '../DownloadButton';
import DownOutlined from '@/assets/icon/down-outlined.svg?react';

const { RangePicker } = DatePicker;
const { Option } = Select;

// 筛选参数接口
export interface FilterChangeParams {
  [key: string]: any;
  dateRange?: [string, string] | null;
}

// 筛选选项接口
export interface FilterOption {
  value: string | number;
  label: string;
}

// 筛选配置接口
export interface FilterConfig {
  key: string;
  placeholder: string;
  options: FilterOption[];
  defaultValue?: string | number;
  defaultValueFn?: () => string | number;
  allowClear?: boolean; // 是否显示清除图标，默认为 true
  minWidth?: number;
  changeClear?: string[];
}

// 组件Props接口
interface FilterProps {
  onFilterChange: (filters: FilterChangeParams) => void;
  initialFilters?: FilterChangeParams;
  loading?: boolean;
  filterConfigs: FilterConfig[];
  showDateRange?: boolean;
  showDownload?: boolean;
  /** 下载按钮配置，使用新的DownloadButton组件 */
  downloadProps?: Omit<DownloadButtonProps, 'disabled'>;
  /** @deprecated 使用 downloadProps 替代 */
  onDownload?: () => void;
}

// 获取默认筛选条件
export const getDefaultFilters = (configs: FilterConfig[]): FilterChangeParams => {
  const defaultFilters: FilterChangeParams = {};
  configs.forEach((config) => {
    if (config.defaultValue) {
      defaultFilters[config.key] = config.defaultValue;
    }
    if (config.defaultValueFn) {
      defaultFilters[config.key] = config.defaultValueFn();
    }
  });
  return defaultFilters;
};

export default function Filter({ onFilterChange, initialFilters = {}, loading = false, filterConfigs, showDateRange = true, showDownload = true, downloadProps, onDownload }: FilterProps) {
  // 合并默认值和初始筛选条件
  const getInitialFiltersWithDefaults = () => {
    const filtersWithDefaults: FilterChangeParams = {};

    // 先应用配置中的默认值
    filterConfigs.forEach((config) => {
      if (config.defaultValue) {
        filtersWithDefaults[config.key] = config.defaultValue;
      }
    });

    // 再应用传入的初始筛选条件（会覆盖默认值）
    return { ...filtersWithDefaults, ...initialFilters };
  };

  const [filters, setFilters] = useState<FilterChangeParams>(getInitialFiltersWithDefaults());

  // 处理单个筛选条件变化
  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    const config = filterConfigs.find((config) => config.key === key);
    if (config?.changeClear) {
      config.changeClear.forEach((clearKey) => {
        newFilters[clearKey] = null;
      });
    }
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  // 处理日期范围变化
  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const dateRange = dates ? ([dates[0]?.format('YYYY-MM-DD'), dates[1]?.format('YYYY-MM-DD')] as [string, string]) : null;
    handleFilterChange('dateRange', dateRange);
  };

  // 重置所有筛选条件
  const handleReset = () => {
    const resetFilters = {};
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  return (
    <>
      <GlobalDatePickerStyles />
      <FilterContainer>
        {/* 动态筛选器 */}
        {filterConfigs.map((config) => (
          <FilterDropdown
            key={config.key}
            placeholder={config.placeholder}
            allowClear={config.allowClear !== false} // 默认为 true，除非明确设置为 false
            value={filters[config.key]}
            onChange={(value) => handleFilterChange(config.key, value)}
            disabled={loading}
            suffixIcon={<DownOutlined style={{ fontSize: '12px', color: '#141413' }} />}
            style={{ minWidth: config.minWidth }}
          >
            {config.options.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </FilterDropdown>
        ))}

        {/* 日期范围选择器 */}
        {showDateRange && (
          <DateRangeContainer>
            <StyledRangePicker
              value={filters.dateRange ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])] : null}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              placeholder={['Start date', 'End date']}
              disabled={loading}
              separator="-"
              suffixIcon={<CalendarOutlined style={{ fontSize: '16px', color: '#141413' }} />}
            />
          </DateRangeContainer>
        )}

        {/* 下载按钮 */}
        {showDownload && downloadProps && <DownloadButton {...downloadProps} disabled={loading} />}

        {/* 兼容旧版本的下载按钮 */}
        {showDownload && !downloadProps && onDownload && (
          <LegacyDownloadButton type="primary" icon={<DownloadOutlined />} disabled={loading} onClick={onDownload}>
            Download
          </LegacyDownloadButton>
        )}
      </FilterContainer>
    </>
  );
}

// Styled Components
const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const FilterDropdown = styled(Select)`
  height: 40px;
  border-radius: 10px;

  .ant-select-selector {
    height: 40px !important;
    border: 1px solid rgba(20, 20, 0, 0.2) !important;
    border-radius: 10px !important;
    padding: 0 14px !important;
    font-family: 'Poppins', sans-serif !important;
    font-size: 16px !important;
    font-weight: 500 !important;
    color: rgba(20, 20, 0, 0.5) !important;
  }

  .ant-select-selection-placeholder {
    color: rgba(20, 20, 0, 0.5) !important;
    font-family: 'Poppins', sans-serif !important;
    font-size: 16px !important;
    font-weight: 500 !important;
  }

  &:hover .ant-select-selector {
    border-color: rgba(20, 20, 0, 0.4) !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: rgba(20, 20, 0, 0.6) !important;
    box-shadow: none !important;
  }
`;

const DateRangeContainer = styled.div`
  position: relative;
`;

const StyledRangePicker = styled(RangePicker)`
  height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(20, 20, 0, 0.2);
  padding: 0 14px;
  font-family: 'Poppins', sans-serif;

  .ant-picker-input input {
    font-family: 'Poppins', sans-serif !important;
    font-size: 16px !important;
    font-weight: 500 !important;
    color: rgba(20, 20, 0, 0.5) !important;
  }

  .ant-picker-input input::placeholder {
    color: rgba(20, 20, 0, 0.5) !important;
    font-family: 'Poppins', sans-serif !important;
    font-size: 16px !important;
    font-weight: 500 !important;
  }

  .ant-picker-separator {
    color: rgba(20, 20, 0, 0.5);
  }

  &:hover {
    border-color: rgba(20, 20, 0, 0.4);
  }

  &.ant-picker-focused,
  &:focus,
  &:focus-within,
  &.ant-picker-active {
    border-color: #141413 !important;
    box-shadow: 0 0 0 2px rgba(20, 20, 0, 0.2) !important;
    outline: none !important;
  }

  /* 覆盖 Ant Design 默认的激活状态样式 */
  &.ant-picker-focused .ant-picker-active-bar {
    background-color: #141413 !important;
  }
`;

/* 全局样式覆盖 DatePicker 面板 */
const GlobalDatePickerStyles = createGlobalStyle`
  /* 覆盖全局 DatePicker 面板样式 */
  .ant-picker-dropdown {
    .ant-picker-panel-container {
      /* 选中的日期颜色 */
      .ant-picker-cell-selected .ant-picker-cell-inner {
        background-color: #A6EB7A !important;
        color: #141413 !important;
      }

      /* 今日的颜色 */
      .ant-picker-cell-today .ant-picker-cell-inner {
        &:before {
          border-color: #A6EB7A !important;
        }
      }

      /* 选择区间的颜色 - 覆盖区间背景 */
      .ant-picker-cell-in-range {
        position: relative;
        
        &::before {
          background-color: rgba(166, 235, 122, 0.3) !important;
        }
      }

      /* 区间开始日期的背景延伸 */
      .ant-picker-cell-range-start:not(.ant-picker-cell-range-end) {
        &::before {
          background-color: rgba(166, 235, 122, 0.3) !important;
        }
      }

      /* 区间结束日期的背景延伸 */
      .ant-picker-cell-range-end:not(.ant-picker-cell-range-start) {
        &::before {
           background-color: rgba(166, 235, 122, 0.3) !important;
        }
      }

      /* 区间开始和结束的颜色 */
      .ant-picker-cell-range-start .ant-picker-cell-inner,
      .ant-picker-cell-range-end .ant-picker-cell-inner {
        background-color: #A6EB7A !important;
        color: #141413 !important;
      }

      /* hover 状态 */
      .ant-picker-cell:hover .ant-picker-cell-inner {
        background-color: rgba(166, 235, 122, 0.5) !important;
        color: #141413 !important;
      }

      /* 选中状态的 hover */
      .ant-picker-cell-selected:hover .ant-picker-cell-inner {
        background-color: #A6EB7A !important;
        color: #141413 !important;
      }

      /* 今日选中状态 */
      .ant-picker-cell-today.ant-picker-cell-selected .ant-picker-cell-inner {
        background-color: #A6EB7A !important;
        color: #141413 !important;
        border-color: #A6EB7A !important;
      }

      /* 覆盖区间边框样式 */
      .ant-picker-cell-range-start:not(.ant-picker-cell-range-end)::before,
      .ant-picker-cell-range-end:not(.ant-picker-cell-range-start)::before,
      .ant-picker-cell-in-range::before {
        border-radius: 0 !important;
      }

      /* 单独选择同一天时的样式 */
      .ant-picker-cell-range-start.ant-picker-cell-range-end .ant-picker-cell-inner {
        background-color: #A6EB7A !important;
        color: #141413 !important;
      }
    }
  }
`;

const LegacyDownloadButton = styled(Button)`
  height: 40px;
  background-color: #141413;
  border-color: #141413;
  border-radius: 28px;
  padding: 0 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: rgba(20, 20, 0, 0.8) !important;
    border-color: rgba(20, 20, 0, 0.8) !important;
  }

  &:focus {
    background-color: #141413 !important;
    border-color: #141413 !important;
  }
`;
